import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Row, Col, Image, Space } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService, LoginCredentials } from '../services/authService';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Two independent messages, deliberately not one piece of state (issue #120).
  // `error` explains a sign-in attempt — wrong password, wrong verification
  // code — and must survive until the customer acts again. `captchaError`
  // belongs to the verification-code image and is owned by its loader. They
  // used to share `error`, and because a failed sign-in reloads the code, the
  // loader wiped the reason for the failure a few hundred milliseconds after it
  // appeared: the message flashed up and vanished before it could be read.
  const [error, setError] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<{ captchaId: string; imageUrl: string } | null>(null);
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  // Set when a CAPTCHA load fails, to stop the mount effect re-arming itself.
  // Cleared whenever a load is started deliberately (the Refresh button, or a
  // reload after a failed sign-in).
  const [captchaFailed, setCaptchaFailed] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  // Get the intended destination from navigation state
  const from = (location.state as any)?.from?.pathname || '/';
  const shouldRedirectAuthenticatedUser =
    auth.isAuthenticated && !auth.isGuest && auth.isSessionValid();

  useEffect(() => {
    // Registered users should not stay on the login page.
    // Guest users need access to sign in mid-checkout.
    if (shouldRedirectAuthenticatedUser) {
      navigate(from, { replace: true });
      return; // Don't load CAPTCHA if redirecting
    }
    
    // Load verification code only once on mount.
    //
    // captchaFailed is what makes "only once" true on the failure path. This
    // effect depends on isLoadingCaptcha, which loadVerificationCode both sets
    // and clears, so it re-runs after every attempt. On success verificationCode
    // stops it; on failure that stays null, so without this flag the effect
    // fires again immediately and retries forever — enough to exhaust the
    // client-side rate limiter and leave the page unusable.
    if (!verificationCode && !isLoadingCaptcha && !captchaFailed) {
      loadVerificationCode();
    }
  }, [shouldRedirectAuthenticatedUser, verificationCode, isLoadingCaptcha, captchaFailed, navigate, from]);
  
  // Separate effect for navigation to avoid CAPTCHA reload
  useEffect(() => {
    if (shouldRedirectAuthenticatedUser) {
      navigate(from, { replace: true });
    }
  }, [shouldRedirectAuthenticatedUser, navigate, from]);

  const loadVerificationCode = async () => {
    // Prevent multiple simultaneous CAPTCHA loads
    if (isLoadingCaptcha) {
      console.log('[Login] CAPTCHA already loading, skipping');
      return;
    }
    
    setIsLoadingCaptcha(true);
    // Starting a load deliberately clears the failure block, so Refresh works
    // after an earlier attempt failed.
    setCaptchaFailed(false);
    setCaptchaError(null);
    try {
      const codeData = await authService.getVerificationCode();
      setVerificationCode({
        captchaId: codeData.captchaId,
        imageUrl: codeData.imageUrl
      });
      // Note what is *not* here: clearing `error`. A new code image says
      // nothing about why the last sign-in failed.
    } catch (error) {
      console.error('Failed to load verification code:', error);
      setCaptchaFailed(true);
      setCaptchaError('Failed to load the verification code. Use Refresh to try again.');
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  const handleLogin = async (values: LoginCredentials) => {
    setLoading(true);
    setError(null);

    if (!verificationCode?.captchaId) {
      setError('CAPTCHA not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    try {
      const loginData = {
        ...values,
        captchaId: verificationCode.captchaId
      };
      const authData = await authService.login(loginData);
      const success = await auth.login(authData);
      if (success) {
        navigate(from, { replace: true });
      } else {
        setError('Login validation failed. Please try again.');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
      // Reload verification code on error
      loadVerificationCode();
      // Clear the form verification code field
      form.setFieldValue('verifyCode', '');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestCheckout = async () => {
    setLoading(true);
    setError(null);

    try {
      const guestSession = await authService.createGuestSession();
      const success = await auth.loginAsGuest(guestSession);
      if (success) {
        navigate('/checkout', { replace: true });
      } else {
        setError('Guest session validation failed. Please try again.');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create guest session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col xs={22} sm={16} md={12} lg={8} xl={6}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={2}>Sign In</Title>
              <Text type="secondary">Welcome back to Dasoon Eggs</Text>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 16 }}
              />
            )}

            {captchaError && (
              <Alert
                message={captchaError}
                type="warning"
                showIcon
                closable
                onClose={() => setCaptchaError(null)}
                style={{ marginBottom: 16 }}
              />
            )}

            <Form
              form={form}
              name="login"
              onFinish={handleLogin}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="userId"
                label="User ID or Email"
                rules={[
                  { required: true, message: 'Please input your user ID or email!' },
                  { max: 50, message: 'Must be less than 50 characters' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Enter your user ID or email"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please input your password!' },
                  { max: 100, message: 'Password must be less than 100 characters' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item label="Verification Code">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {verificationCode && verificationCode.imageUrl ? (
                    <div style={{ textAlign: 'center' }}>
                      <Image
                        src={verificationCode.imageUrl}
                        alt="Verification Code"
                        style={{ border: '1px solid #d9d9d9', borderRadius: 4 }}
                        preview={false}
                      />
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '20px',
                      background: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      Loading CAPTCHA...
                    </div>
                  )}
                  
                  <Form.Item
                    name="verifyCode"
                    rules={[
                      { required: true, message: 'Please input the verification code!' },
                      { max: 10, message: 'Verification code must be less than 10 characters' }
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      prefix={<SafetyOutlined />}
                      placeholder="Enter verification code"
                      addonAfter={
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={() => loadVerificationCode()}
                          style={{ padding: 0 }}
                        >
                          Refresh
                        </Button>
                      }
                    />
                  </Form.Item>
                </Space>
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                >
                  Sign In
                </Button>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="default"
                  onClick={handleGuestCheckout}
                  loading={loading}
                  block
                  size="large"
                >
                  Continue as Guest
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
                <Button type="link" size="small" onClick={() => navigate('/forgot-password')}>
                  Forgot Password?
                </Button>
                <Button type="link" size="small" onClick={() => navigate('/register')}>
                  Create Account
                </Button>
              </Space>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
